import { ReactNode } from "react";
import { ArrowUp, ArrowDownIcon, Minus } from "@credcore/core-ui/icons";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn-components/Card";
import { Skeleton } from "@/components/shadcn-components/Skeleton";
import { cn } from "@/lib/utils";
import { formatNumber, formatPercent } from "@/lib/utils";

interface MetricCardProps {
  title: string;
  value: number | string;
  change?: number;
  changeLabel?: string;
  format?: "number" | "percent" | "custom";
  icon?: ReactNode;
  loading?: boolean;
  className?: string;
}

export function MetricCard({
  title,
  value,
  change,
  changeLabel,
  format = "number",
  icon,
  loading,
  className,
}: MetricCardProps) {
  if (loading) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            <Skeleton className="h-4 w-24" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="mb-1 h-8 w-32" />
          <Skeleton className="h-3 w-20" />
        </CardContent>
      </Card>
    );
  }

  const formattedValue =
    format === "percent"
      ? formatPercent(Number(value))
      : format === "number"
        ? formatNumber(Number(value))
        : value;

  const getTrendIcon = () => {
    if (change === undefined || change === 0)
      return <Minus className="h-4 w-4 text-gray-400" />;
    if (change > 0) return <ArrowUp className="h-4 w-4 text-green-500" />;
    return <ArrowDownIcon className="h-4 w-4 text-red-500" />;
  };

  const getTrendColor = () => {
    if (change === undefined || change === 0) return "text-gray-500";
    if (change > 0) return "text-green-600";
    return "text-red-600";
  };

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">
          {title}
        </CardTitle>
        {icon && <div className="text-gray-400">{icon}</div>}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{formattedValue}</div>
        {change !== undefined && (
          <div className="mt-1 flex items-center gap-1">
            {getTrendIcon()}
            <span className={cn("text-xs", getTrendColor())}>
              {change > 0 ? "+" : ""}
              {formatPercent(change, 1)}
              {changeLabel && ` ${changeLabel}`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
